/* Replacement Assistant — one page for ALL of a teacher's outstanding
   replacements. Smart Adjacent suggestions are pre-computed for every affected
   student (de-duplicated so two students never get the same time). The teacher
   reviews, adjusts and sends everything in one batch. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { useList, errMsg } from "@/lib/api";
import { PageHeader, Spinner, EmptyState, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SpecialSlotDialog } from "@/pages/reschedule/ReschedulePages";
import { toast } from "sonner";
import { Sparkles, Send, CheckCheck, ExternalLink, UserRound, Save, HandHelping } from "lucide-react";

const slotKey = (s) => (s ? `${s.date}|${s.start_time}|${s.end_time}` : "");
const slotLabel = (s) =>
  `${s.date} ${s.start_time}–${s.end_time}${s.outside_normal_availability ? " · outside normal hours" : ""}`;

export default function ReplacementAssistantPage() {
  const { data, loading, reload } = useList("/replacement-assistant");
  const [chosen, setChosen] = useState({});   // request_id -> suggestion
  const [selected, setSelected] = useState({}); // request_id -> bool
  const [results, setResults] = useState({});  // request_id -> {ok, error}
  const [specialOpen, setSpecialOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const rows = data?.rows || [];

  useEffect(() => {
    if (!data?.rows) return;
    const init = {};
    for (const r of data.rows) {
      const draft = r.draft && r.draft.date
        ? { date: r.draft.date, start_time: r.draft.start_time, end_time: r.draft.end_time,
            outside_normal_availability: !!r.draft.open_special_slot }
        : null;
      init[r.request_id] = draft || r.best || null;
    }
    setChosen(init);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const options = (r) => {
    const opts = [];
    const seen = new Set();
    for (const s of [r.best, ...(r.alternatives || [])].filter(Boolean)) {
      if (seen.has(slotKey(s))) continue;
      seen.add(slotKey(s));
      opts.push(s);
    }
    return opts;
  };

  const sendable = rows.filter((r) => selected[r.request_id] && chosen[r.request_id]);
  const specialCount = sendable.filter((r) => chosen[r.request_id]?.outside_normal_availability).length;

  const useBestForAll = () => {
    const sel = {}, ch = { ...chosen };
    for (const r of rows) {
      if (r.best && !r.open_proposal) { sel[r.request_id] = true; ch[r.request_id] = r.best; }
    }
    setSelected(sel); setChosen(ch);
    toast.success(`${Object.keys(sel).length} student(s) selected with their best suggestion`);
  };

  const send = async (openSpecials = false) => {
    if (sendable.length === 0) { toast.error("Select at least one student with a chosen time"); return; }
    if (!openSpecials && specialCount > 0) { setSpecialOpen(true); return; }
    setBusy(true);
    try {
      const items = sendable.map((r) => ({
        request_id: r.request_id,
        date: chosen[r.request_id].date,
        start_time: chosen[r.request_id].start_time,
        end_time: chosen[r.request_id].end_time,
        open_special_slot: !!chosen[r.request_id].outside_normal_availability,
      }));
      const { data: res } = await api.post("/replacement-assistant/send", { items });
      const map = {};
      for (const it of res.results) map[it.request_id] = it;
      setResults(map);
      setSelected({});
      setSpecialOpen(false);
      if (res.sent > 0) toast.success(`${res.sent} proposal(s) sent — parents will confirm`);
      if (res.failed > 0) toast.error(`${res.failed} proposal(s) could not be sent — see the rows below`);
      reload();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const askParent = async (r) => {
    try {
      await api.post(`/replacement-assistant/${r.request_id}/ask-parent`);
      toast.success(`${r.student_name}'s family asked to choose a time`);
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveDraft = async (r) => {
    const c = chosen[r.request_id];
    if (!c) { toast.error("Pick a time first"); return; }
    try {
      await api.post(`/replacement-assistant/${r.request_id}/draft`, {
        date: c.date, start_time: c.start_time, end_time: c.end_time,
        open_special_slot: !!c.outside_normal_availability,
      });
      toast.success("Draft saved — send it later");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="replacement-assistant-page">
      <PageHeader title="Replacement Assistant"
        description="Every affected student in one place — smart suggestions are pre-computed and de-duplicated. Review, adjust, and send all proposals together. Unresolved requests never expire."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={useBestForAll} disabled={busy || rows.length === 0}
              data-testid="use-best-for-all-button">
              <CheckCheck className="h-4 w-4 mr-1" />Use best suggestions for all
            </Button>
            <Button onClick={() => send(false)} disabled={busy || sendable.length === 0}
              data-testid="send-selected-button">
              <Send className="h-4 w-4 mr-1" />
              {busy ? "Sending..." : `Send ${sendable.length || ""} selected proposal(s)`}
            </Button>
          </div>
        } />

      {loading ? <Spinner full /> : rows.length === 0 ? (
        <EmptyState text="No students waiting for a replacement time. When your leave is approved, every affected student appears here automatically." />
      ) : (
        <div className="space-y-3 stagger-in">
          {rows.map((r) => {
            const c = chosen[r.request_id];
            const res = results[r.request_id];
            return (
              <div key={r.request_id}
                className={`rounded-lg border bg-card p-4 ${res && !res.ok ? "border-destructive/50" : ""}`}
                data-testid={`assistant-row-${r.request_id}`}>
                <div className="flex flex-wrap items-center gap-3">
                  <label className="flex items-center gap-2.5 min-w-[220px] flex-1 cursor-pointer">
                    <input type="checkbox" checked={!!selected[r.request_id]}
                      disabled={!c || !!r.open_proposal}
                      onChange={(e) => setSelected((p) => ({ ...p, [r.request_id]: e.target.checked }))}
                      data-testid={`assistant-select-${r.request_id}`} />
                    <span>
                      <span className="text-sm font-semibold flex items-center gap-1.5">
                        <UserRound className="h-3.5 w-3.5 text-secondary" />{r.student_name}
                        <span className="text-xs text-muted-foreground font-normal">· {r.course_name}</span>
                      </span>
                      <span className="text-xs text-muted-foreground font-mono">
                        missed {r.missed_date} {r.missed_start_time}–{r.missed_end_time} · {r.duration_minutes} min owed
                      </span>
                    </span>
                  </label>

                  {r.open_proposal ? (
                    <span className="text-xs px-2 py-1 rounded-full bg-[#EAF3EE] text-[#2D6A4F] font-semibold"
                      data-testid={`assistant-proposal-status-${r.request_id}`}>
                      Proposal sent — waiting for parent
                      {r.open_proposal.is_special_slot ? " (special slot)" : ""}
                    </span>
                  ) : options(r).length === 0 ? (
                    <span className="text-xs px-2 py-1 rounded-full bg-[#FFEDEC] text-destructive font-semibold">
                      No clean time found — open the request or ask the parent
                    </span>
                  ) : (
                    <Select value={slotKey(c)} onValueChange={(v) => {
                      const s = options(r).find((o) => slotKey(o) === v);
                      setChosen((p) => ({ ...p, [r.request_id]: s }));
                    }}>
                      <SelectTrigger className="w-[300px] h-9 text-xs font-mono"
                        data-testid={`assistant-time-select-${r.request_id}`}>
                        <SelectValue placeholder="Choose a suggested time..." />
                      </SelectTrigger>
                      <SelectContent>
                        {options(r).map((s, i) => (
                          <SelectItem key={slotKey(s)} value={slotKey(s)} className="text-xs font-mono">
                            {i === 0 ? "★ " : ""}{slotLabel(s)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}

                  {c?.outside_normal_availability && !r.open_proposal && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold">
                      will open a special one-time slot
                    </span>
                  )}
                  {r.parent_pick_requested && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-800 font-semibold">
                      parent asked to choose
                    </span>
                  )}
                  <StatusBadge status={r.status} />

                  <div className="flex items-center gap-1.5">
                    {!r.open_proposal && (
                      <>
                        <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => saveDraft(r)}
                          data-testid={`assistant-draft-${r.request_id}`}>
                          <Save className="h-3.5 w-3.5 mr-1" />Draft
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => askParent(r)}
                          data-testid={`assistant-ask-parent-${r.request_id}`}>
                          <HandHelping className="h-3.5 w-3.5 mr-1" />Ask parent
                        </Button>
                      </>
                    )}
                    <Link to={`/teacher/reschedule/${r.request_id}`}
                      className="inline-flex items-center text-xs text-secondary font-semibold hover:underline"
                      data-testid={`assistant-open-${r.request_id}`}>
                      <ExternalLink className="h-3.5 w-3.5 mr-0.5" />Open
                    </Link>
                  </div>
                </div>
                {res && !res.ok && (
                  <p className="text-xs text-destructive mt-2" data-testid={`assistant-error-${r.request_id}`}>
                    {res.error}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-6 rounded-lg border border-dashed p-4 text-xs text-muted-foreground flex items-start gap-2">
        <Sparkles className="h-4 w-4 text-secondary shrink-0 mt-0.5" />
        <span>
          Suggestions attach a double lesson before/after each student's next normal class within 60 days.
          The engine blocks double-booking, trial/replacement/group clashes, approved leave, studio clashes and
          times where the full duration doesn't fit. Times outside your normal availability are sent as
          Teacher Special One-Time Slots after your confirmation.
        </span>
      </div>

      <SpecialSlotDialog open={specialOpen} busy={busy}
        onConfirm={() => send(true)} onCancel={() => setSpecialOpen(false)} />
    </div>
  );
}
